import { useState, useEffect, useRef } from "react";
import { MagnifyingGlass, NavigationArrow, CaretRight, MapPin, Star } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { searchPlaces, reverseGeocode } from "@/lib/api";
import { toast } from "sonner";
import {
  FAVOURITES_EVENT,
  getFavourites,
  isFavourite,
  toggleFavourite,
} from "@/lib/favourites";

/**
 * LocationBar — slim top bar with current city + search trigger + GPS.
 * Tapping it opens a full-screen search overlay with an inline auto-focused
 * input + live results list.
 */
export default function LocationBar({ location, onSelect, theme }) {
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [locating, setLocating] = useState(false);
  const [favs, setFavs] = useState(() => getFavourites());
  const [overlayHeight, setOverlayHeight] = useState(null);
  const debounceRef = useRef();
  const inputRef = useRef(null);

  useEffect(() => {
    const refresh = () => setFavs(getFavourites());
    window.addEventListener(FAVOURITES_EVENT(), refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener(FAVOURITES_EVENT(), refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  // Track the visual viewport so the search overlay shrinks when the
  // soft keyboard opens. Without this, on Android the keyboard overlays
  // the bottom of the list and users can't scroll to see results.
  useEffect(() => {
    if (!searchOpen) return;
    const vv = window.visualViewport;
    if (!vv) {
      setOverlayHeight(null);
      return;
    }
    const sync = () => setOverlayHeight(vv.height);
    sync();
    vv.addEventListener("resize", sync);
    vv.addEventListener("scroll", sync);
    return () => {
      vv.removeEventListener("resize", sync);
      vv.removeEventListener("scroll", sync);
    };
  }, [searchOpen]);

  const pinned = isFavourite(location);

  const togglePin = (e) => {
    e?.stopPropagation();
    if (!location?.latitude || !location?.longitude) return;
    const updated = toggleFavourite(location);
    setFavs(updated);
    if (updated.some((f) => f.name === location.name)) {
      toast.success(`Pinned ${location.name}`);
    } else {
      toast.success(`Unpinned ${location.name}`);
    }
  };

  useEffect(() => {
    if (!searchOpen) return;
    // Auto-focus input as soon as the overlay opens
    const id = setTimeout(() => inputRef.current?.focus(), 80);
    return () => clearTimeout(id);
  }, [searchOpen]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query || query.trim().length < 2) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await searchPlaces(query.trim());
        setResults(r);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 220);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  const useGPS = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not supported");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const r = await reverseGeocode(pos.coords.latitude, pos.coords.longitude);
          onSelect({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            name: r?.name || "Current location",
            admin1: r?.admin1 || null,
            country: r?.country || null,
            timezone: r?.timezone || null,
          });
          setSearchOpen(false);
          toast.success(`Located: ${r?.name || "your position"}`);
        } catch {
          onSelect({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude,
            name: "Current location",
          });
          setSearchOpen(false);
        } finally {
          setLocating(false);
        }
      },
      (err) => {
        setLocating(false);
        toast.error("Unable to get location: " + err.message);
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const darkMood = theme.id === "night" || theme.id === "storm";
  const overlayBg = darkMood ? "rgba(15,23,42,0.96)" : "rgba(255,255,255,0.96)";
  const inputBg = darkMood ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)";
  const inputBorder = darkMood ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.10)";
  const itemHover = darkMood ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)";

  return (
    <>
      <div className="relative w-full">
      <button
        type="button"
        onClick={() => setSearchOpen(true)}
        data-testid="location-bar"
        className="w-full flex items-center gap-2.5 px-3.5 py-2 rounded-full"
        style={{
          background: theme.chip,
          border: `1px solid ${theme.surfaceBorder}`,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
        }}
      >
        <NavigationArrow size={13} weight="fill" style={{ color: theme.accent }} />
        <div className="flex-1 min-w-0 text-left">
          <div
            className="text-[13px] font-semibold truncate leading-tight"
            style={{ color: theme.textPrimary }}
            data-testid="location-bar-name"
          >
            {location?.name || "Find a place"}
          </div>
          {location?.admin1 && (
            <div
              className="text-[10px] leading-tight truncate"
              style={{ color: theme.textMuted }}
            >
              {location.admin1}
              {location.country ? ` · ${location.country}` : ""}
            </div>
          )}
        </div>
        <MagnifyingGlass size={14} weight="bold" style={{ color: theme.textMuted }} />
      </button>
      {/* Pin/unpin floating button — only when a location is set */}
      {location?.latitude && location?.longitude && (
        <button
          type="button"
          onClick={togglePin}
          aria-label={pinned ? "Unpin location" : "Pin location"}
          data-testid="location-pin-toggle"
          className="absolute -top-1 -right-1 w-7 h-7 rounded-full flex items-center justify-center transition-all active:scale-90"
          style={{
            background: pinned ? theme.accent : theme.chip,
            color: pinned
              ? darkMood ? "#0f172a" : "#ffffff"
              : theme.textMuted,
            border: `1.5px solid ${pinned ? theme.accent : theme.surfaceBorder}`,
            boxShadow: pinned ? `0 4px 10px ${theme.accent}55` : "none",
          }}
        >
          <Star size={11} weight={pinned ? "fill" : "bold"} />
        </button>
      )}
      </div>

      <AnimatePresence>
        {searchOpen && (
          <motion.div
            className="fixed left-0 right-0 top-0 z-50 flex flex-col"
            style={{
              height: overlayHeight ? `${overlayHeight}px` : "100dvh",
              maxHeight: "100dvh",
              background: overlayBg,
              backdropFilter: "blur(24px)",
              WebkitBackdropFilter: "blur(24px)",
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.22 }}
            data-testid="location-search-overlay"
          >
            {/* Header */}
            <div className="px-4 pt-4 pb-3 flex items-center gap-2 shrink-0">
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                aria-label="Close search"
                data-testid="location-search-close"
                className="w-9 h-9 rounded-full flex items-center justify-center rotate-180"
                style={{ background: inputBg, color: theme.textPrimary }}
              >
                <CaretRight size={14} weight="bold" />
              </button>
              <div
                className="text-[11px] uppercase tracking-[0.2em] font-medium"
                style={{ color: theme.textMuted }}
              >
                Find a place
              </div>
            </div>

            {/* Inline search input */}
            <div className="px-4 shrink-0">
              <div
                className="flex items-center gap-2.5 px-4 py-3 rounded-2xl"
                style={{
                  background: inputBg,
                  border: `1px solid ${inputBorder}`,
                }}
              >
                <MagnifyingGlass size={16} weight="bold" style={{ color: theme.accent }} />
                <input
                  ref={inputRef}
                  data-testid="location-search-input"
                  type="text"
                  inputMode="search"
                  placeholder="Type a city, suburb, or place…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-[15px]"
                  style={{ color: theme.textPrimary }}
                />
                <button
                  type="button"
                  onClick={useGPS}
                  disabled={locating}
                  data-testid="use-my-location-btn"
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-full disabled:opacity-50"
                  style={{
                    background: theme.accent,
                    color: darkMood ? "#0f172a" : "#fff",
                  }}
                >
                  {locating ? "…" : "GPS"}
                </button>
              </div>
            </div>

            {/* Favourites strip — horizontal scrolling chips. Hidden once user
                starts typing so it doesn't compete with live results. */}
            {favs.length > 0 && query.trim().length < 2 && (
              <div
                className="px-4 mt-3 shrink-0"
                data-testid="favourites-strip"
              >
                <div
                  className="text-[10px] uppercase tracking-[0.18em] font-semibold mb-2 px-1 flex items-center gap-1.5"
                  style={{ color: theme.textMuted }}
                >
                  <Star size={10} weight="fill" style={{ color: theme.accent }} />
                  Pinned places
                </div>
                <div
                  className="flex gap-2 overflow-x-auto pb-1.5 -mx-4 px-4"
                  style={{
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                    overscrollBehavior: "contain",
                  }}
                >
                  {favs.map((f) => (
                    <button
                      key={`${f.id ?? "x"}-${f.latitude}-${f.longitude}`}
                      type="button"
                      data-testid={`favourite-chip-${f.name.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => {
                        onSelect(f);
                        setSearchOpen(false);
                        setQuery("");
                      }}
                      className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full active:scale-95 transition-transform"
                      style={{
                        background: inputBg,
                        border: `1px solid ${inputBorder}`,
                        color: theme.textPrimary,
                        maxWidth: 200,
                      }}
                    >
                      <Star
                        size={10}
                        weight="fill"
                        style={{ color: theme.accent, flexShrink: 0 }}
                      />
                      <span className="text-[12px] font-medium truncate">{f.name}</span>
                      {f.admin1 && (
                        <span
                          className="text-[10px] truncate"
                          style={{ color: theme.textMuted }}
                        >
                          · {f.admin1}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Results — only this region scrolls */}
            <div
              className="flex-1 overflow-y-auto px-4 pt-3 pb-6"
              style={{ overscrollBehavior: "contain" }}
              data-testid="location-search-results"
            >
              {searching && (
                <div className="text-[12px] px-2 py-3" style={{ color: theme.textMuted }}>
                  Searching…
                </div>
              )}
              {!searching && query.trim().length >= 2 && results.length === 0 && (
                <div className="text-[13px] px-2 py-3" style={{ color: theme.textMuted }}>
                  No matches. Try another spelling.
                </div>
              )}
              {results.map((r) => (
                <button
                  key={`${r.id}-${r.latitude}-${r.longitude}`}
                  type="button"
                  data-testid={`location-result-${r.id}`}
                  onClick={() => {
                    onSelect(r);
                    setSearchOpen(false);
                    setQuery("");
                    setResults([]);
                  }}
                  className="w-full flex items-start gap-3 px-3 py-2.5 rounded-xl text-left transition-colors"
                  style={{ color: theme.textPrimary }}
                  onMouseEnter={(e) => (e.currentTarget.style.background = itemHover)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <MapPin
                    size={16}
                    weight="duotone"
                    className="mt-0.5 shrink-0"
                    style={{ color: theme.accent }}
                  />
                  <div className="flex-1 min-w-0">
                    <div
                      className="text-[14px] font-medium truncate"
                      style={{ color: theme.textPrimary }}
                    >
                      {r.name}
                      {r.admin2 && (
                        <span
                          className="font-normal"
                          style={{ color: theme.textMuted }}
                        >
                          {" · "}
                          {r.admin2}
                        </span>
                      )}
                    </div>
                    <div
                      className="text-[11px] truncate"
                      style={{ color: theme.textMuted }}
                    >
                      {[r.admin1, r.country].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
